import {
  badWords,
  fileExtension,
  fileName,
  maxFileSize,
  maxLength,
  noEmptyFile,
  required,
  requiredFile,
  requiredSelection,
} from '@seek/validators-js';

import { useConfig } from 'src/context/config';
import { FileAttachment } from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { createFieldValidator } from 'src/forms/validator';

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
export const FILE_EXT = ['.pdf', '.doc', '.docx', '.rtf', '.txt'];
export const MAX_CHARS = 5000;
export const METHOD_VALIDATORS = [requiredSelection];
export const TEXTAREA_VALIDATORS = [required, badWords, maxLength];
export const FILE_UPLOAD_VALIDATORS = [
  requiredFile,
  fileName,
  fileExtension,
  noEmptyFile,
  maxFileSize,
];
export const FILE_SELECT_VALIDATORS = [requiredSelection];

interface UseValidatorsArgs {
  currentMethod: FileAttachment;
}

export function useValidators({ currentMethod }: UseValidatorsArgs) {
  const { config } = useConfig();

  const validateFile = async (file: File, formValues = {}) => {
    if (FileAttachment.UPLOAD !== currentMethod) return;
    const validators = createFieldValidator(
      FILE_UPLOAD_VALIDATORS,
      {
        maxFileSize: MAX_FILE_SIZE,
        allowedExtensions: FILE_EXT,
      },
      config.LANGUAGE,
    );

    let errorMsg: string | undefined;

    try {
      errorMsg = await validators(file, formValues);
    } catch (e) {
      errorMsg = (e as Error).message || 'unknown error';
    }
    return errorMsg;
  };

  const validateText = async (text: string, formValues = {}) => {
    if (FileAttachment.CHANGE !== currentMethod) return;
    const textValidators = createFieldValidator(
      TEXTAREA_VALIDATORS,
      { max: MAX_CHARS },
      config.LANGUAGE,
    );

    let errorMsg: string | undefined;

    try {
      errorMsg = await textValidators(text, formValues);
    } catch (e) {
      errorMsg = (e as Error).message || 'unknown error';
    }
    return errorMsg;
  };

  const validateFileSelection = async (id: string, formValues = {}) => {
    if (FileAttachment.CHANGE !== currentMethod) return;

    const validators = createFieldValidator(
      FILE_SELECT_VALIDATORS,
      {},
      config.LANGUAGE,
    );

    let errorMsg: string | undefined;

    try {
      errorMsg = await validators(id ? [id] : [], formValues);
    } catch (e) {
      errorMsg = (e as Error).message || 'unknown error';
    }

    return errorMsg;
  };

  return {
    validateMethod: createFieldValidator(
      METHOD_VALIDATORS,
      {},
      config.LANGUAGE,
    ),
    validateFile,
    validateText,
    validateFileSelection,
  };
}
